import Base from '@components/layouts/base';
import AboveTheFoldContent from '@components/partials/category/aboveTheFoldContent';
import CategoryFooterSeo from '@components/partials/category/categoryFooterSeo';
import CategoryTopSites from '@components/partials/category/categoryTopSites';
import ContentDescription from '@components/partials/common/contentDescription';
import Footer from '@components/partials/common/footer';
import MainTitle from '@components/partials/common/mainTitle';
import Topbar from '@components/partials/common/topbar';
import Header from '@components/partials/header/header';
import PaginationSite from '@components/partials/pagination/paginationSite';
import CategoryCloud from '@components/partials/site/categoryCloud';
import SiteList from '@components/partials/site/list';
import { getCategoryList } from '@data/category';
import { getCategoryReview } from '@data/categoryReview';
import { getIndexData } from '@data/index';
import { getLanguageList } from '@data/languageList';
import redirect404 from 'lib/helper/404redirect';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { IndexProps } from '../../lib/types/page/indexprops';
export default function Index({
    pageData,
    category,
    categoryList,
    categoryReviewData,
    categoryPagination,
    languageList,
    currentLang,
    locales
}: {
    pageData: IndexProps;
    category: string;
    categoryList: any;
    categoryReviewData: any;
    categoryPagination: any;
    languageList: any;
    currentLang: string;
    locales: string[];
}): JSX.Element {
    if (!pageData) return <div>Loading ...</div>;
    const router = useRouter();

    useEffect(() => {
        redirect404(router);
    }, []);

    return (
        <>
            <Base>
                <Head>
                    <title>{categoryReviewData.categoryInfo.pageTitle}</title>
                    <meta
                        name="description"
                        content={categoryReviewData.categoryInfo.metaDescription}
                    />
                    <meta name="robots" content="index, follow" />
                    <link
                        rel="canonical"
                        href={
                            process.env.NEXT_PUBLIC_APP_DOMAIN +
                            (currentLang != 'en' ? currentLang + '/' : '') +
                            category +
                            '/'
                        }
                    />
                    <link
                        rel="alternate"
                        href={process.env.NEXT_PUBLIC_APP_DOMAIN + category + '/'}
                        hrefLang="x-default"
                    />
                    {categoryReviewData.pageLangs.map((lang: any) => {
                        const isReviewPublished = lang.isReviewPublished;
                        const langPath = lang.lang !== 'en' ? lang.lang + '/' : '';
                        const languageInfo = languageList.find(
                            (language: any) => language.slug === lang.lang
                        );
                        const hrefLang = languageInfo ? languageInfo.hreflang : lang.lang;

                        return isReviewPublished ? (
                            <link
                                key={lang.lang}
                                rel="alternate"
                                href={`${process.env.NEXT_PUBLIC_APP_DOMAIN}${langPath}${category}/`}
                                hrefLang={hrefLang}
                            />
                        ) : null;
                    })}
                    <link
                        href="https://maxcdn.bootstrapcdn.com/font-awesome/4.6.3/css/font-awesome.min.css"
                        rel="stylesheet"
                        type="text/css"
                    />
                </Head>
                <div className="wrapper">
                    <Topbar
                        categoryList={categoryList}
                        homeSlug={'/' + currentLang}
                        languageList={categoryReviewData.pageLangs}
                        isHomePage={false}
                    />
                    <Header intro={categoryReviewData.categoryInfo.intro} introEmail={'email'} />
                    <MainTitle title={categoryReviewData.categoryInfo.name} />
                    <PaginationSite
                        categoryName={categoryReviewData.categoryInfo.name}
                        categorySlug={category}
                        pagination={categoryPagination}
                    />
                    {categoryReviewData.categoryInfo.topContent !== '' ? (
                        <>
                            <AboveTheFoldContent
                                content={categoryReviewData.categoryInfo.topContent}
                                currentLang={currentLang}
                            />
                            <CategoryTopSites
                                topSiteDataList={categoryReviewData.topCategorySites}
                                categoryName={categoryReviewData.categoryInfo.name}
                                activeSites={categoryList[category].activeSites}
                                currentLang={currentLang}
                            />
                        </>
                    ) : (
                        <ContentDescription
                            content={categoryReviewData.categoryInfo.content}
                            isCategory={true}
                            currentLang={currentLang}
                        />
                    )}

                    {categoryList[category] && categoryList[category].activeSites.length > 0 && (
                        <SiteList
                            categoryData={categoryReviewData}
                            isCategory={true}
                            currentLang={currentLang}
                        />
                    )}
                    <CategoryCloud
                        categoryData={categoryReviewData}
                        title={categoryReviewData.categoryInfo.cloudTitle}
                    />
                </div>
                {categoryReviewData.categoryInfo.topContent !== '' && (
                    <CategoryFooterSeo
                        miniReview={categoryReviewData.categoryInfo.footerMiniReviews}
                        seoText={categoryReviewData.categoryInfo.footerSeo}
                        faq={categoryReviewData.categoryInfo.footerFAQ}
                        currentLang={currentLang}
                    />
                )}

                <Footer />
            </Base>
        </>
    );
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: [
            { params: { category: 'sex-roulette-sites' } },
            { params: { category: 'free-cam-sites' } },
            { params: { category: 'voyeur-cams' } },
            { params: { category: 'live-cam-tubes' } },
            { params: { category: 'top-cam-sites' } }
        ],
        fallback: 'blocking'
    };
};

// Get the data required to populate the page
export const getStaticProps: GetStaticProps = async (context: any) => {
    const category = context.params.category.toString();
    const currentLang = context.locale;
    const locales = context.locales;

    const data = await getCategoryList(currentLang);

    const categoryPagination = data.categoryPagination;
    const categoryList = data.categoryList;

    const categoryReviewData = await getCategoryReview(category, currentLang);
    const languageList = getLanguageList();

    if (
        categoryReviewData.pageLangs.length === 0 ||
        !categoryReviewData.categoryInfo.isReviewPublished
    ) {
        return {
            notFound: true
        };
    }
  
    // Get the data from the respository to populate the page
    const pageData = await getIndexData();
    return {
        props: {
            pageData,
            category,
            categoryList,
            categoryReviewData,
            categoryPagination,
            languageList,
            currentLang,
            locales
        },
        revalidate: 300
    };
};
